import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import AppNav from "../components/appNav";
import Footer from "../components/footer";
import { useTranslation } from "react-i18next";
import { submitGrantApplication } from "../firebase";

type FormStep = 1 | 2 | 3 | 4;

interface FormData {
  fullName: string;
  email: string;
  phone: string;
  location: string;
  linkedIn: string;
  startupName: string;
  website: string;
  industry: string;
  stage: string;
  problemStatement: string;
  solution: string;
  targetMarket: string;
  uniqueValue: string;
  traction: string;
  revenue: string;
  teamSize: string;
  cofounderBackground: string;
  preferredTier: "standard" | "strong";
  whyGrant: string;
  commitment: string;
}

const initialFormData: FormData = {
  fullName: "",
  email: "",
  phone: "",
  location: "",
  linkedIn: "",
  startupName: "",
  website: "",
  industry: "",
  stage: "",
  problemStatement: "",
  solution: "",
  targetMarket: "",
  uniqueValue: "",
  traction: "",
  revenue: "",
  teamSize: "",
  cofounderBackground: "",
  preferredTier: "strong",
  whyGrant: "",
  commitment: "",
};

const Field = ({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) => (
  <label className="flex flex-col">
    <span className="swiss-caption mb-2">
      {label}
      {required && " *"}
    </span>
    {children}
  </label>
);

const GrantApplicationPage = () => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState<FormStep>(1);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<{ success: boolean; message: string } | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const updateField = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateStep = (step: FormStep): boolean => {
    switch (step) {
      case 1:
        return !!(formData.fullName && formData.email && formData.phone && formData.location);
      case 2:
        return !!(formData.startupName && formData.industry && formData.stage);
      case 3:
        return !!(
          formData.problemStatement &&
          formData.solution &&
          formData.targetMarket &&
          formData.uniqueValue
        );
      case 4:
        return !!(formData.teamSize && formData.whyGrant && formData.commitment);
      default:
        return false;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep) && currentStep < 4) {
      setCurrentStep((prev) => (prev + 1) as FormStep);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep((prev) => (prev - 1) as FormStep);
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) return;
    setIsSubmitting(true);
    try {
      const result = await submitGrantApplication({ ...formData, source: "website" });
      setSubmitResult(
        result.success
          ? { success: true, message: t("grantApplication.success.message") }
          : { success: false, message: result.error || t("grantApplication.error.message") }
      );
    } catch {
      setSubmitResult({ success: false, message: t("grantApplication.error.message") });
    } finally {
      setIsSubmitting(false);
    }
  };

  const industries = [
    "SaaS / Software",
    "E-commerce",
    "Fintech",
    "Healthcare / Healthtech",
    "Education / Edtech",
    "Travel / Tourism",
    "Real Estate / Proptech",
    "Food & Beverage",
    "Sustainability / Cleantech",
    "AI / Machine Learning",
    "Marketplace",
    "Other",
  ];

  const stages = [
    { value: "idea", label: t("grantApplication.stages.idea") },
    { value: "validation", label: t("grantApplication.stages.validation") },
    { value: "prototype", label: t("grantApplication.stages.prototype") },
    { value: "beta", label: t("grantApplication.stages.beta") },
    { value: "launched", label: t("grantApplication.stages.launched") },
  ];

  const teamSizes = [
    { value: "solo", label: t("grantApplication.teamSizes.solo") },
    { value: "2", label: t("grantApplication.teamSizes.two") },
    { value: "3-5", label: t("grantApplication.teamSizes.three_five") },
    { value: "5+", label: t("grantApplication.teamSizes.five_plus") },
  ];

  if (submitResult?.success) {
    return (
      <>
        <Helmet>
          <title>{t("grantApplication.successPage.title")} | KODE</title>
        </Helmet>
        <div className="min-h-screen bg-paper text-ink">
          <header className="sticky top-0 z-50 bg-paper/95 backdrop-blur-[2px] border-b border-ink-14">
            <AppNav />
          </header>
          <main className="swiss-section !border-t-0">
            <div className="swiss-grid">
              <div className="col-span-12 lg:col-span-2 mb-6 lg:mb-0">
                <span className="swiss-num">— / {t("Done")}</span>
              </div>
              <div className="col-span-12 lg:col-span-10">
                <h1 className="text-h1 font-bold tracking-tighter text-ink mb-5 max-w-[18ch]">
                  {t("grantApplication.successPage.heading")}
                </h1>
                <p className="text-body-lg text-ink-70 max-w-prose mb-7">
                  {t("grantApplication.successPage.description")}
                </p>
                <Link to="/mvp-build" className="btn btn-primary">
                  {t("grantApplication.successPage.backButton")}
                </Link>
              </div>
            </div>
            <Footer />
          </main>
        </div>
      </>
    );
  }

  const inputBase =
    "w-full px-0 py-3 bg-transparent border-0 border-b border-ink-14 focus:border-ink focus:outline-none text-ink";

  return (
    <>
      <Helmet>
        <title>{t("grantApplication.pageTitle")} | KODE</title>
        <meta name="description" content={t("grantApplication.pageDescription")} />
      </Helmet>

      <div className="min-h-screen bg-paper text-ink">
        <header className="sticky top-0 z-50 bg-paper/95 backdrop-blur-[2px] border-b border-ink-14">
          <AppNav />
        </header>

        <main className="swiss-section !border-t-0 pt-7 lg:pt-9">
          <div className="swiss-grid">
            <div className="col-span-12 lg:col-span-2 mb-6 lg:mb-0">
              <span className="swiss-num">— / {t("Application")}</span>
            </div>

            <div className="col-span-12 lg:col-span-10">
              <Link
                to="/mvp-build"
                className="swiss-caption link-underline text-ink mb-7 inline-block"
              >
                ← {t("grantApplication.backToProgram")}
              </Link>

              <h1 className="text-h1 font-bold tracking-tighter text-ink mb-5 max-w-[20ch]">
                {t("grantApplication.title")}
              </h1>
              <p className="text-body-lg text-ink-70 max-w-prose mb-9">
                {t("grantApplication.subtitle")}
              </p>

              {/* Step indicator */}
              <ol className="grid grid-cols-4 gap-6 border-b border-ink-14 pb-7 mb-9">
                {[1, 2, 3, 4].map((step) => (
                  <li
                    key={step}
                    className={`border-t pt-3 ${
                      step <= currentStep ? "border-ink" : "border-ink-14"
                    }`}
                  >
                    <span
                      className={`swiss-num ${
                        step === currentStep
                          ? "text-ink"
                          : step < currentStep
                          ? "text-ink-50"
                          : "text-ink-30"
                      }`}
                    >
                      {String(step).padStart(2, "0")}
                    </span>
                  </li>
                ))}
              </ol>

              <div className="max-w-2xl">
                {currentStep === 1 && (
                  <div className="flex flex-col gap-6">
                    <h2 className="text-h3 font-medium tracking-tight text-ink mb-2">
                      {t("grantApplication.step1.title")}
                    </h2>
                    <Field label={t("grantApplication.fields.fullName")} required>
                      <input
                        type="text"
                        value={formData.fullName}
                        onChange={(e) => updateField("fullName", e.target.value)}
                        className={inputBase}
                        placeholder={t("grantApplication.placeholders.fullName")}
                      />
                    </Field>
                    <Field label={t("grantApplication.fields.email")} required>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => updateField("email", e.target.value)}
                        className={inputBase}
                        placeholder={t("grantApplication.placeholders.email")}
                      />
                    </Field>
                    <Field label={t("grantApplication.fields.phone")} required>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => updateField("phone", e.target.value)}
                        className={inputBase}
                        placeholder={t("grantApplication.placeholders.phone")}
                      />
                    </Field>
                    <Field label={t("grantApplication.fields.location")} required>
                      <input
                        type="text"
                        value={formData.location}
                        onChange={(e) => updateField("location", e.target.value)}
                        className={inputBase}
                        placeholder={t("grantApplication.placeholders.location")}
                      />
                    </Field>
                    <Field label={t("grantApplication.fields.linkedIn")}>
                      <input
                        type="url"
                        value={formData.linkedIn}
                        onChange={(e) => updateField("linkedIn", e.target.value)}
                        className={inputBase}
                        placeholder={t("grantApplication.placeholders.linkedIn")}
                      />
                    </Field>
                  </div>
                )}

                {currentStep === 2 && (
                  <div className="flex flex-col gap-6">
                    <h2 className="text-h3 font-medium tracking-tight text-ink mb-2">
                      {t("grantApplication.step2.title")}
                    </h2>
                    <Field label={t("grantApplication.fields.startupName")} required>
                      <input
                        type="text"
                        value={formData.startupName}
                        onChange={(e) => updateField("startupName", e.target.value)}
                        className={inputBase}
                        placeholder={t("grantApplication.placeholders.startupName")}
                      />
                    </Field>
                    <Field label={t("grantApplication.fields.website")}>
                      <input
                        type="url"
                        value={formData.website}
                        onChange={(e) => updateField("website", e.target.value)}
                        className={inputBase}
                        placeholder={t("grantApplication.placeholders.website")}
                      />
                    </Field>
                    <Field label={t("grantApplication.fields.industry")} required>
                      <select
                        value={formData.industry}
                        onChange={(e) => updateField("industry", e.target.value)}
                        className={inputBase}
                      >
                        <option value="">{t("grantApplication.placeholders.selectIndustry")}</option>
                        {industries.map((industry) => (
                          <option key={industry} value={industry}>
                            {industry}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label={t("grantApplication.fields.stage")} required>
                      <select
                        value={formData.stage}
                        onChange={(e) => updateField("stage", e.target.value)}
                        className={inputBase}
                      >
                        <option value="">{t("grantApplication.placeholders.selectStage")}</option>
                        {stages.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </Field>
                  </div>
                )}

                {currentStep === 3 && (
                  <div className="flex flex-col gap-6">
                    <h2 className="text-h3 font-medium tracking-tight text-ink mb-2">
                      {t("grantApplication.step3.title")}
                    </h2>
                    <Field label={t("grantApplication.fields.problemStatement")} required>
                      <textarea
                        value={formData.problemStatement}
                        onChange={(e) => updateField("problemStatement", e.target.value)}
                        rows={3}
                        className={inputBase}
                        placeholder={t("grantApplication.placeholders.problemStatement")}
                      />
                    </Field>
                    <Field label={t("grantApplication.fields.solution")} required>
                      <textarea
                        value={formData.solution}
                        onChange={(e) => updateField("solution", e.target.value)}
                        rows={3}
                        className={inputBase}
                        placeholder={t("grantApplication.placeholders.solution")}
                      />
                    </Field>
                    <Field label={t("grantApplication.fields.targetMarket")} required>
                      <textarea
                        value={formData.targetMarket}
                        onChange={(e) => updateField("targetMarket", e.target.value)}
                        rows={2}
                        className={inputBase}
                        placeholder={t("grantApplication.placeholders.targetMarket")}
                      />
                    </Field>
                    <Field label={t("grantApplication.fields.uniqueValue")} required>
                      <textarea
                        value={formData.uniqueValue}
                        onChange={(e) => updateField("uniqueValue", e.target.value)}
                        rows={2}
                        className={inputBase}
                        placeholder={t("grantApplication.placeholders.uniqueValue")}
                      />
                    </Field>
                  </div>
                )}

                {currentStep === 4 && (
                  <div className="flex flex-col gap-6">
                    <h2 className="text-h3 font-medium tracking-tight text-ink mb-2">
                      {t("grantApplication.step4.title")}
                    </h2>
                    <Field label={t("grantApplication.fields.teamSize")} required>
                      <select
                        value={formData.teamSize}
                        onChange={(e) => updateField("teamSize", e.target.value)}
                        className={inputBase}
                      >
                        <option value="">{t("grantApplication.placeholders.selectTeamSize")}</option>
                        {teamSizes.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label={t("grantApplication.fields.cofounderBackground")}>
                      <textarea
                        value={formData.cofounderBackground}
                        onChange={(e) => updateField("cofounderBackground", e.target.value)}
                        rows={2}
                        className={inputBase}
                        placeholder={t("grantApplication.placeholders.cofounderBackground")}
                      />
                    </Field>
                    <Field label={t("grantApplication.fields.traction")}>
                      <textarea
                        value={formData.traction}
                        onChange={(e) => updateField("traction", e.target.value)}
                        rows={2}
                        className={inputBase}
                        placeholder={t("grantApplication.placeholders.traction")}
                      />
                    </Field>
                    <Field label={t("grantApplication.fields.revenue")}>
                      <input
                        type="text"
                        value={formData.revenue}
                        onChange={(e) => updateField("revenue", e.target.value)}
                        className={inputBase}
                        placeholder={t("grantApplication.placeholders.revenue")}
                      />
                    </Field>

                    <fieldset>
                      <legend className="swiss-caption mb-4">
                        {t("grantApplication.fields.preferredTier")} *
                      </legend>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {(["standard", "strong"] as const).map((tier) => (
                          <button
                            key={tier}
                            type="button"
                            onClick={() => updateField("preferredTier", tier)}
                            className={`text-left p-5 border transition-colors ${
                              formData.preferredTier === tier
                                ? "border-ink bg-ink text-paper"
                                : "border-ink-14 hover:border-ink"
                            }`}
                          >
                            <div className="text-h4 font-medium tracking-tight mb-2">
                              {t(`mvpBuilderProgram.grant.tiers.${tier}.title`)}
                            </div>
                            <div className="swiss-caption">
                              {t(`mvpBuilderProgram.grant.tiers.${tier}.youPay`)} ·{" "}
                              {t(`mvpBuilderProgram.grant.tiers.${tier}.equity`)}
                            </div>
                          </button>
                        ))}
                      </div>
                    </fieldset>

                    <Field label={t("grantApplication.fields.whyGrant")} required>
                      <textarea
                        value={formData.whyGrant}
                        onChange={(e) => updateField("whyGrant", e.target.value)}
                        rows={3}
                        className={inputBase}
                        placeholder={t("grantApplication.placeholders.whyGrant")}
                      />
                    </Field>
                    <Field label={t("grantApplication.fields.commitment")} required>
                      <textarea
                        value={formData.commitment}
                        onChange={(e) => updateField("commitment", e.target.value)}
                        rows={2}
                        className={inputBase}
                        placeholder={t("grantApplication.placeholders.commitment")}
                      />
                    </Field>
                  </div>
                )}

                {submitResult && !submitResult.success && (
                  <div className="mt-6 border-t border-signal pt-4">
                    <p className="text-body text-signal">{submitResult.message}</p>
                  </div>
                )}

                <div className="flex items-center justify-between mt-9 pt-7 border-t border-ink-14">
                  {currentStep > 1 ? (
                    <button
                      type="button"
                      onClick={prevStep}
                      className="swiss-caption link-underline text-ink"
                    >
                      ← {t("grantApplication.buttons.back")}
                    </button>
                  ) : (
                    <div />
                  )}

                  {currentStep < 4 ? (
                    <button
                      type="button"
                      onClick={nextStep}
                      disabled={!validateStep(currentStep)}
                      className="btn btn-primary disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      {t("grantApplication.buttons.next")} →
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={!validateStep(4) || isSubmitting}
                      className="btn btn-primary disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      {isSubmitting
                        ? t("grantApplication.buttons.submitting")
                        : t("grantApplication.buttons.submit")}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default GrantApplicationPage;
